import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Phone } from "lucide-react";
import axios from "axios";
import { BUSINESS } from "@/data/businessInfo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: `Hi there! I'm the Southern Light Solutions assistant. I can answer questions about our electrical services, help you book an appointment, or connect you with our team. How can I help you today?` }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const messagesEnd = useRef(null);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: userMsg });
      setMessages(prev => [...prev, { role: "bot", text: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "bot", text: `I'm having trouble connecting right now. Please call us at ${BUSINESS.phone} for immediate assistance.` }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Chat button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-4 z-50 p-3.5 btn-gradient rounded-full shadow-xl chat-pulse"
          data-testid="chatbot-toggle"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-6 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-[#6B3A1F]/15 flex flex-col overflow-hidden" style={{ height: '480px', maxHeight: 'calc(100vh - 8rem)' }} data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#BF3604] via-[#D97904] to-[#BF8D30] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Southern Light Assistant</p>
                <p className="text-white/60 text-xs">Online now</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white p-1" data-testid="chatbot-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#BF3604] to-[#D97904] text-white rounded-br-md'
                    : 'bg-gray-100 text-[#401303] rounded-bl-md'
                }`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#6B3A1F]/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#6B3A1F]/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#6B3A1F]/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          {/* Input */}
          <div className="border-t border-[#6B3A1F]/10 px-3 py-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2.5 text-sm border-2 border-[#6B3A1F]/15 rounded-full focus:border-[#D97904] focus:outline-none transition-colors duration-200"
                data-testid="chatbot-input"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              />
              <button onClick={send} disabled={loading || !input.trim()} className="p-2.5 btn-gradient rounded-full disabled:opacity-40" data-testid="chatbot-send">
                <Send className="w-4 h-4" />
              </button>
            </div>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center justify-center gap-1.5 mt-2 text-xs text-[#6B3A1F] hover:text-[#BF8D30] transition-colors duration-200" data-testid="chatbot-call-link">
              <Phone className="w-3 h-3" /> Or call {BUSINESS.phone}
            </a>
          </div>
        </div>
      )}
    </>
  );
};
